/**
 * Subscription Actions Component
 * 
 * Provides activation and cancellation buttons with confirmation flows.
 * Handles API integration and user feedback for subscription operations.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useSubscription, useSubscriptionActions } from '../hooks/useSubscription';
import { useTranslation } from '../../i18n/hooks';
import { Button } from '../../ui';
import './SubscriptionActions.css';

/**
 * Subscription Actions Component
 * @param {Object} props - Component props
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.showActivate - Whether to show activation button
 * @param {boolean} props.showCancel - Whether to show cancellation button
 * @param {Function} props.onActivate - Callback for successful activation
 * @param {Function} props.onCancel - Callback for successful cancellation
 * @param {boolean} props.compact - Whether to show compact version
 * @returns {JSX.Element} Subscription actions component
 */
const SubscriptionActions = ({
  className = '',
  showActivate = true,
  showCancel = true,
  onActivate,
  onCancel,
  compact = false
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { status, loading } = useSubscription();
  const { cancel } = useSubscriptionActions();
  
  // Modal states (only for cancellation now, activation goes directly to Stripe)
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [operationLoading, setOperationLoading] = useState(false);
  const [operationError, setOperationError] = useState(null);

  /**
   * Handle activation button click - Navigate directly to Stripe checkout
   * No confirmation modal needed since Stripe handles the payment flow
   */
  const handleActivateClick = useCallback(() => {
    // Navigate directly to Stripe subscription page
    navigate('/subscription');
    // Call optional callback if provided
    if (onActivate) {
      onActivate();
    }
  }, [navigate, onActivate]);

  /**
   * Handle cancellation button click
   */
  const handleCancelClick = useCallback(() => {
    setOperationError(null);
    setShowCancelModal(true);
  }, []);

  /**
   * Confirm cancellation
   */
  const handleConfirmCancellation = async () => {
    setOperationLoading(true);
    setOperationError(null);
    
    try {
      await cancel();
      setShowCancelModal(false);
      if (onCancel) {
        onCancel();
      }
    } catch (error) {
      console.error('Cancellation failed:', error);
      setOperationError(error.message || t('subscription.errors.cancellationFailed'));
    } finally {
      setOperationLoading(false);
    }
  };

  /**
   * Close modals
   */
  const handleCloseModals = useCallback(() => {
    setShowCancelModal(false);
    setOperationError(null);
  }, []);

  // Handle click outside modal to close
  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      handleCloseModals();
    }
  }, [handleCloseModals]);

  // Handle body scroll lock when modals are open
  useEffect(() => {
    if (showCancelModal) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [showCancelModal]);

  // Memoize computed values to prevent unnecessary re-renders
  const isActive = useMemo(() => status === 'active', [status]);
  const isInactive = useMemo(() => 
    status === 'inactive' || status === 'expired' || status === 'cancelled', 
    [status]
  );

  const CancellationModal = useMemo(() => {
    if (!showCancelModal) return null;
    
    return createPortal(
      <div 
        id="subscription-cancel-modal-overlay"
        className="subscription-modal-overlay" 
        onClick={handleOverlayClick}
      >
        <div 
          id="subscription-cancel-modal"
          className="subscription-modal subscription-modal-cancellation"
        >
          <div className="subscription-modal-content">
            <div className="subscription-modal-header">
              <div className="subscription-modal-icon">
                <svg className="subscription-warning-icon" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
              </div>
              <h3 className="subscription-modal-title">
                {t('subscription.modals.cancel.title')}
              </h3>
              <p className="subscription-modal-message">
                {t('subscription.modals.cancel.message')}
              </p>
              
              {operationError && (
                <div className="subscription-modal-error">
                  <p className="subscription-error-text">
                    {operationError}
                  </p>
                </div>
              )}
            </div>
            
            <div className="subscription-modal-actions">
              <button 
                className="subscription-modal-btn subscription-modal-btn-secondary"
                onClick={handleCloseModals}
                disabled={operationLoading}
              >
                {t('subscription.modals.keep')}
              </button>
              <button 
                className="subscription-modal-btn subscription-modal-btn-danger"
                onClick={handleConfirmCancellation}
                disabled={operationLoading}
              >
                {operationLoading ? t('subscription.actions.cancelling') : t('subscription.modals.cancel.confirm')}
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  }, [showCancelModal, operationError, operationLoading, t, handleOverlayClick, handleCloseModals, handleConfirmCancellation]);

  if (compact) {
    return (
      <div className={`subscription-actions-compact ${className}`}>
        {isInactive && showActivate && (
          <Button
            variant="primary"
            size="sm"
            onClick={handleActivateClick}
            disabled={loading.activation || operationLoading}
            className="subscription-activate-btn"
          >
            {loading.activation || operationLoading ? t('subscription.actions.activating') : t('subscription.actions.activate')}
          </Button>
        )}
        
        {isActive && showCancel && (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleCancelClick}
            disabled={loading.deactivation || operationLoading}
            className="subscription-cancel-btn"
          >
            {loading.deactivation || operationLoading ? t('subscription.actions.cancelling') : t('subscription.actions.cancel')}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={`subscription-actions ${className}`}>
      {/* Action Buttons */}
      <div className="subscription-actions-buttons">
        {isInactive && showActivate && (
          <Button
            variant="primary"
            onClick={handleActivateClick}
            disabled={loading.activation || operationLoading}
            className="subscription-activate-btn"
          >
            {loading.activation || operationLoading ? t('subscription.actions.activating') : t('subscription.actions.activate')}
          </Button>
        )}
        
        {isActive && showCancel && (
          <Button
            variant="secondary"
            onClick={handleCancelClick}
            disabled={loading.deactivation || operationLoading}
            className="subscription-cancel-btn"
          >
            {loading.deactivation || operationLoading ? t('subscription.actions.cancelling') : t('subscription.actions.cancel')}
          </Button>
        )}
      </div>

      {/* Portal-based Modals (only cancellation modal, activation goes directly to Stripe) */}
      {CancellationModal}
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders
const MemoizedSubscriptionActions = React.memo(SubscriptionActions);
export { MemoizedSubscriptionActions as SubscriptionActions };

SubscriptionActions.propTypes = {
  className: PropTypes.string,
  showActivate: PropTypes.bool,
  showCancel: PropTypes.bool,
  onActivate: PropTypes.func,
  onCancel: PropTypes.func,
  compact: PropTypes.bool
};

export default SubscriptionActions;
